<template>
  <div class="pdf-viewer" :class="{ 'fullscreen': isFullScreen }" ref="pdfViewerContainer">
    <i class="full-screen-button fas fa-expand" :class="{ 'fixed': isFullScreen }" @click="toggleFullScreen"></i>
    <div class="pdf-content" :style="{ height: isFullScreen ? '100vh' : 'auto' }">
      <vue-pdf
        ref="pdfViewer"
        :src="pdfUrl"
        :page="currentPage"
        @num-pages="updateNumPages"
        :style="pdfStyle"
        :quality="10000"
        class="vue-pdf-preview"
      ></vue-pdf>
    </div>
    <div v-if="isFullScreen" class="toolbar">
      <div class="toolbar-left">
        <button class="toolbar-btn" @click.stop="rotateClockwise"><i class="fas fa-redo"></i></button>
        <button class="toolbar-btn" @click.stop="rotateCounterClockwise"><i class="fas fa-undo"></i></button>
        <button class="toolbar-btn" @click.stop="prevPage"><i class="far fa-chevron-left"></i></button>
        <button class="toolbar-btn" @click.stop="nextPage"><i class="far fa-chevron-right"></i></button>
        <button class="toolbar-btn" @click.stop="zoomIn"><i class="fas fa-search-plus"></i></button>
        <button class="toolbar-btn" @click.stop="zoomOut"><i class="fas fa-search-minus"></i></button>
        <button class="toolbar-btn" @click.stop="resetPosition"><i class="fas fa-sync"></i></button>
      </div>
      <div class="toolbar-center">
        <span class="toolbar-counter">{{ currentPage }}/{{ numPages }}</span>
        <span class="toolbar-zoom">Zoom: {{ zoomLevel }}%</span>
      </div>
      <div class="toolbar-right">
        <button class="toolbar-action-btn toolbar-action-primary" @click="toggleFullScreen">
          <i class="fas fa-compress"></i> {{ $t('ui.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import VuePdf from 'vue-pdf';
import firebase from 'firebase/app';
import interact from 'interactjs';

export default {
  name: 'PdfViewer',
  components: {
    VuePdf
  },
  props: ['pdfFile'],
  data() {
    return {
      pdfUrl: null,
      numPages: 0,
      currentPage: 1,
      isFullScreen: false,
      zoomLevel: 100,
      rotation: 0,
      posX: 0,
      posY: 0,
    };
  },
  watch: {
    pdfFile: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.getFileUrl();
        }
      }
    }
  },
  computed: {
    pdfStyle() {
      const scale = this.zoomLevel / 100;
      return {
        transform: `scale(${scale}) rotate(${this.rotation}deg)`,
        transformOrigin: 'center center',
      };
    }
  },
  methods: {
    rotateClockwise() {
      this.rotation += 90;
      if (this.rotation >= 360) {
        this.rotation -= 360;
      }
    },
    rotateCounterClockwise() {
      this.rotation -= 90;
      if (this.rotation <= -360) {
        this.rotation += 360;
      }
    },
    toggleFullScreen() {
  if (!this.isFullScreen) {
    // Entering fullscreen mode
    this.resetPosition(); // Reset position when entering fullscreen
    this.currentPage = 1; // Go to the first page when entering fullscreen
    document.body.style.overflow = 'hidden'; // Disable scrolling
    this.$refs.pdfViewerContainer.style.overflow = 'hidden'; // Disable scrolling in the container
    this.enableDragging(true); // Enable dragging for repositioning
  } else {
    // Exiting fullscreen mode
    document.body.style.overflow = ''; // Enable scrolling
    this.$refs.pdfViewerContainer.style.overflow = ''; // Enable scrolling in the container
    this.enableDragging(false); // Disable dragging
    this.resetZoomAndPosition(); // Reset zoom and position
    this.currentPage = 1; // Go to the first page when exiting fullscreen
    // Ensure the full-screen button is visible
    document.querySelector('.full-screen-button').style.display = 'block';
  }
  this.isFullScreen = !this.isFullScreen;
},


resetScrollPosition() {
  const pdfContent = this.$refs.pdfViewerContainer.querySelector('.pdf-content');
  pdfContent.scrollTop = 0;
},




    zoomIn() {
      this.zoomLevel += 10;
    },
    zoomOut() {
      this.zoomLevel = Math.max(10, this.zoomLevel - 10);
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.numPages) {
        this.currentPage++;
      }
    },
    updateNumPages(numPages) {
      this.numPages = numPages;
    },
    async getFileUrl() {
      try {
        if (!this.pdfFile.file && this.pdfFile.url) {
          this.pdfUrl = this.pdfFile.url;
        } else if (this.pdfFile.file) {
          const reader = new FileReader();
          reader.readAsDataURL(this.pdfFile.file);
          reader.onload = (e) => {
            this.pdfUrl = e.target.result;
          };
        } else {
          const url = await this.getDownloadURLFromBucket(this.pdfFile.fullPath);
          this.pdfUrl = url;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error getting file URL:', error);
      }
    },
    async getDownloadURLFromBucket(path) {
      const fileRef = firebase.storage().ref(path);
      return fileRef.getDownloadURL();
    },
    enableDragging(enable) {
      if (enable) {
        interact('.pdf-content').draggable({
          inertia: true,
          onmove: event => {
            const target = event.target;
            let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
            let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;
            target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
            this.posX = x;
            this.posY = y;
          },
          autoScroll: true,
        });
      } else {
        interact('.pdf-content').unset();
      }
    },
    resetPosition() {
      this.resetZoomAndPosition();
    },
    resetZoomAndPosition() {
      this.zoomLevel = 100;
      const pdfContent = this.$refs.pdfViewerContainer.querySelector('.pdf-content');
      pdfContent.style.transform = 'translate(0px, 0px)';
      pdfContent.setAttribute('data-x', 0);
      pdfContent.setAttribute('data-y', 0);
      this.posX = 0;
      this.posY = 0;
    },
  },
  mounted() {
    this.getFileUrl();
  },
};
</script>

<style scoped>
.pdf-viewer::-webkit-scrollbar {
  width: 0px;
}
.pdf-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0;
  margin-bottom: 10px;
  padding: 0;
  overflow: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.vue-pdf-preview {
  width: 100%;
}
.pdf-viewer.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background-color: grey;
}
.pdf-content {
  margin: 0;
  height: 100%;
}
.file-viewer {
  height: 100%;
  max-height: unset;
  background-color: lightgray;
}

.toolbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F8F9FA;
  padding: 12px 16px;
  z-index: 10000;
  border-top: 1px solid #CED4DA;
  box-shadow: 0 -3px 8px rgba(0, 0, 0, 0.06);
}

.toolbar-left {
  display: flex;
  gap: 8px;
}

.toolbar-center {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-counter {
  font-weight: bold;
  font-size: 18px;
  color: #272B30;
}

.toolbar-zoom {
  font-size: 14px;
  color: #ADB5BD;
}

.toolbar-right {
  display: flex;
  gap: 8px;
}

.toolbar-btn {
  background-color: white;
  color: black;
  border: none;
  padding: 5px 10px;
  margin: 0;
  border-radius: 4px;
  cursor: pointer;
  height: 6rem;
  width: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.08);
  transition: background-color 0.3s;
  font-size: 18px;
}

.toolbar-btn:hover {
  background-color: #888;
  color: white;
}

.toolbar-action-btn {
  padding: 0 32px;
  border-radius: 4px;
  font-size: 20px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 20px;
  white-space: nowrap;
  height: 60px;
  letter-spacing: 0.25px;
}

.toolbar-action-primary {
  background: #2a52be;
  color: white;
  border: none;
  border-bottom: 2px solid #193172;
}

.toolbar-action-primary:hover {
  background: #1e3fa0;
}

.full-screen-button {
  position: sticky;
  right: 20px;
  z-index: 2;
  left: 97%;
  top: 5%;
  cursor: pointer;
  font-size: 25px;
  color: #00c9ff;
  text-shadow: 1px 2px rgba(0,0, 0, 0.4);
  display: unset !important;
}
.full-screen-button.fixed {
  position: fixed;
  right: 20px;
  top: 20px;
  left: 95%;

}


.full-screen-button fas fa-expand { 
  display: block; 
  left: 47rem;
}
</style>
